import React, { useMemo } from 'react';
import { Avatar, Tooltip, Group, Text, Stack, Indicator } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { ProductType } from '../types';
import { usePresence } from '../hooks/usePresence';

interface CurrentViewersProps {
  type: ProductType;
  id: string;
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  withEyeIcon?: boolean;
  showCurrentUser?: boolean;
  showCount?: boolean;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const stringToColor = (str: string): string => {
  const colors = [
    '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6',
    '#15aabf', '#12b886', '#40c057', '#82c91e', '#fab005',
    '#fd7e14', '#fa5252'
  ];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

export const CurrentViewers: React.FC<CurrentViewersProps> = ({
  type,
  id,
  maxVisible = 3,
  size = 'sm',
  withEyeIcon = false,
  showCurrentUser = false,
  showCount = false,
}) => {
  // This hook automatically uses the optimized presence manager
  const { viewers, viewerCount, otherViewers, otherViewerCount } = usePresence(type, id);

  // Filter viewers based on showCurrentUser prop
  const displayViewers = useMemo(() => {
    return showCurrentUser ? viewers : otherViewers;
  }, [viewers, otherViewers, showCurrentUser]);

  const displayCount = showCurrentUser ? viewerCount : otherViewerCount;

  // Split into visible and hidden
  const visibleViewers = useMemo(() => 
    displayViewers.slice(0, maxVisible), 
    [displayViewers, maxVisible]
  );
  
  const hiddenViewers = useMemo(() => 
    displayViewers.slice(maxVisible), 
    [displayViewers, maxVisible]
  );

  if (!displayViewers || displayViewers.length === 0) {
    return null;
  }

  const overflowTooltipContent = hiddenViewers.length > 0 ? (
    <Stack gap={4}>
      {hiddenViewers.map((viewer) => (
        <Text key={viewer.id} size="xs">
          {viewer.name}
          {viewer.isCurrentUser && ' (You)'}
          {viewer.isStale && ' •'}
        </Text>
      ))}
    </Stack>
  ) : null;

  return (
    <Group gap={4} wrap="nowrap">
      {withEyeIcon && (
        <IconEye 
          size={size === 'xs' ? 14 : size === 'sm' ? 16 : size === 'md' ? 18 : 20} 
          opacity={0.6}
        />
      )}

      <Avatar.Group spacing={size === 'xs' ? 6 : size === 'sm' ? 8 : size === 'md' ? 10 : 12}>
        {visibleViewers.map((viewer) => (
          <Tooltip 
            key={viewer.id} 
            label={
              <div>
                <Text size="xs">{viewer.name}</Text>
                {viewer.isCurrentUser && <Text size="xs" c="dimmed">You</Text>}
                {viewer.isStale && <Text size="xs" c="yellow">Connection may be stale</Text>}
              </div>
            } 
            withArrow
          >
            <Indicator
              disabled={!viewer.isStale}
              color="yellow"
              size={6}
              offset={2}
              position="bottom-end"
            >
              <Avatar
                size={size}
                radius="xl"
                color={viewer.isCurrentUser ? 'blue' : stringToColor(viewer.name)}
                variant={viewer.isCurrentUser ? 'filled' : 'light'}
                styles={{
                  placeholder: {
                    fontSize: size === 'xs' ? '10px' : 
                             size === 'sm' ? '11px' : 
                             size === 'md' ? '13px' : 
                             size === 'lg' ? '16px' : '18px',
                    fontWeight: 600,
                  }
                }}
              >
                {getInitials(viewer.name)}
              </Avatar>
            </Indicator>
          </Tooltip>
        ))}

        {hiddenViewers.length > 0 && (
          <Tooltip label={overflowTooltipContent} withArrow>
            <Avatar
              size={size}
              radius="xl"
              color="gray"
              styles={{
                placeholder: {
                  fontSize: size === 'xs' ? '10px' : 
                           size === 'sm' ? '11px' : 
                           size === 'md' ? '13px' : 
                           size === 'lg' ? '16px' : '18px',
                  fontWeight: 600,
                }
              }}
            >
              +{hiddenViewers.length}
            </Avatar>
          </Tooltip>
        )}
      </Avatar.Group>

      {showCount && displayCount > 0 && (
        <Text size={size === 'xs' ? 'xs' : 'sm'} c="dimmed">
          {displayCount} {displayCount === 1 ? 'viewer' : 'viewers'}
        </Text>
      )}
    </Group>
  );
};